// Contraste según WCAG 2.x (luminancia relativa y ratio (L1 + 0.05) / (L2 + 0.05)).
// AA pide 4.5:1 para texto normal y 3:1 para texto grande y componentes de interfaz.
// Acá se exige 4.5:1 en todas las combinaciones de texto que la app usa con cada token,
// porque los tokens de marca van en botones, links y textos de 12 a 14 px.

import type { ColoresDeBranding } from './catalogo';
import { esHex } from './color';

function canal(valor: number): number {
  const s = valor / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** Luminancia relativa, de 0 (negro) a 1 (blanco). */
export function luminancia(hex: string): number {
  if (!esHex(hex)) throw new Error(`No es un hex de seis dígitos: ${hex}`);
  const n = parseInt(hex.slice(1), 16);
  return 0.2126 * canal((n >> 16) & 255) + 0.7152 * canal((n >> 8) & 255) + 0.0722 * canal(n & 255);
}

export function ratioDeContraste(a: string, b: string): number {
  const la = luminancia(a);
  const lb = luminancia(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export interface ObjecionDeContraste {
  regla: string;
  ratio: number;
  minimo: number;
}

export const BLANCO = '#FFFFFF';
/** `--surface-bg`: la más oscura de las superficies claras sobre las que va texto de marca. */
export const SUPERFICIE_GRIS = '#F0F2F7';
export const AA_TEXTO = 4.5;
/** Un hover que no se distingue del primario deja el botón "muerto" al pasar el mouse. */
export const HOVER_DISTINGUIBLE = 1.1;

/**
 * Las combinaciones en las que la app pone texto sobre cada token, con el mínimo que
 * tienen que cumplir. Devuelve las que no llegan; vacío = la paleta se puede registrar.
 */
export function verificarContraste(c: ColoresDeBranding): ObjecionDeContraste[] {
  const reglas: Array<[regla: string, a: string, b: string, minimo: number]> = [
    // Botón primario con texto blanco, y link primario sobre fondo blanco.
    ['primary sobre blanco', c.primary, BLANCO, AA_TEXTO],
    ['primary sobre la superficie gris', c.primary, SUPERFICIE_GRIS, AA_TEXTO],
    ['primaryHover sobre blanco', c.primaryHover, BLANCO, AA_TEXTO],
    // Fondos profundos (héroes, gradientes) con texto blanco.
    ['deep sobre blanco', c.deep, BLANCO, AA_TEXTO],
    // El color de texto de toda la app.
    ['black sobre blanco', c.black, BLANCO, AA_TEXTO],
    ['black sobre la superficie gris', c.black, SUPERFICIE_GRIS, AA_TEXTO],
    // Texto oscuro sobre el acento (badges) y sobre el acento suave (fondos de aviso).
    ['black sobre accent', c.black, c.accent, AA_TEXTO],
    ['black sobre accentSoft', c.black, c.accentSoft, AA_TEXTO],
    ['hover se distingue del primario', c.primary, c.primaryHover, HOVER_DISTINGUIBLE],
  ];
  return reglas
    .map(([regla, a, b, minimo]) => ({ regla, ratio: ratioDeContraste(a, b), minimo }))
    .filter((o) => o.ratio < o.minimo);
}
